import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClickAwayListener } from '@mui/material'

import RectangleButton, { ButtonSize as RectangleButtonSize } from 'particles/Button/RectangleButton'
import IconButton, { ButtonSize as IconButtonSize } from 'particles/Button/IconButton'
import Icon, { IconSize } from 'particles/Icon'
import Text from 'particles/Text'
import styled from 'particles/themes/styled'
import { useIsMobileView } from 'hooks'

interface SizeMapProps {
    magnifyIconSize: IconSize
    resetIconButtonSize: IconButtonSize
    height: string
    foldedButtonSize: RectangleButtonSize
}
type SearchSize = 's' | 'xs'

const sizeMap: Record<SearchSize, SizeMapProps> = {
    s: {
        magnifyIconSize: '20px',
        resetIconButtonSize: 'xs',
        height: '32px',
        foldedButtonSize: 'm',
    },
    xs: {
        magnifyIconSize: '16px',
        resetIconButtonSize: 'xs',
        height: '28px',
        foldedButtonSize: 's',
    },
}

interface SearchProps extends React.ButtonHTMLAttributes<HTMLInputElement> {
    hasSearchButton?: boolean
    isDisabled?: boolean
    collapsible?: boolean
    placeholder?: string
    searchValue?: string
    size?: SearchSize
    width?: string
    minWidth?: string
    onSearch?: (value: string) => void
    onClearSearch?: () => void
    autoFocus?: boolean
}

const Search = ({
    hasSearchButton = true,
    isDisabled = false,
    collapsible = false,
    onSearch,
    onClearSearch,
    placeholder,
    searchValue = '',
    size = 's',
    width = '100%',
    minWidth = '200px',
    onChange,
    autoFocus: autoFocusProp,
    ...props
}: SearchProps) => {
    const [t] = useTranslation()
    const [value, setValue] = useState('')
    const [isFolded, setIsFolded] = useState(false)
    const { magnifyIconSize, resetIconButtonSize, height, foldedButtonSize } = sizeMap[size]
    const searchInputRef = useRef<HTMLInputElement | null>(null)
    const isMobileView = useIsMobileView()

    useEffect(() => {
        setIsFolded(collapsible)
    }, [collapsible])

    useEffect(() => {
        setValue(searchValue)
    }, [searchValue])

    useEffect(() => {
        const setAutoComplete = setTimeout(() => {
            if (!isFolded && searchInputRef.current) {
                searchInputRef.current.autocomplete = 'on'
            }
        }, 300)
        return () => {
            clearTimeout(setAutoComplete)
        }
    }, [isFolded])

    const handleSearch: React.FormEventHandler<HTMLFormElement> = e => {
        e.preventDefault()
        const trimmedValue = value?.trim()
        onSearch?.(trimmedValue)
    }

    const handleClear = () => {
        setValue('')
        onClearSearch?.()
        if (collapsible) {
            setIsFolded(true)
        }
    }

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value)
        onChange?.(e)
    }

    return (
        <ClickAwayListener
            onClickAway={() => {
                if (collapsible && !value) {
                    setIsFolded(true)
                }
            }}
        >
            <SearchContainer isFolded={isFolded} width={width} minWidth={minWidth}>
                {!isFolded ? (
                    <SearchInputContainer onSubmit={handleSearch} disabled={isDisabled} height={height}>
                        <Icon
                            name="magnifyingGlass"
                            size={magnifyIconSize}
                            color={isDisabled ? 'disable' : 'neutralAction'}
                        />
                        <StyledInput
                            ref={searchInputRef}
                            className="search-input"
                            disabled={isDisabled}
                            placeholder={placeholder}
                            autoFocus={autoFocusProp ?? collapsible}
                            value={value}
                            onChange={handleOnChange}
                            name="keywords"
                            autoComplete="off"
                            {...props}
                        />
                        {value && !isDisabled && (
                            <IconButton
                                onClick={handleClear}
                                type="button"
                                icon="xCircle"
                                form="icon"
                                color="neutral"
                                size={resetIconButtonSize}
                            />
                        )}
                        {!isMobileView && hasSearchButton && (
                            <>
                                <VerticalDivider />
                                <StyledSearchButton type="submit" disabled={isDisabled}>
                                    <Text scale="bodyS700" color={isDisabled ? 'disable' : 'neutralAction'}>
                                        {t('button.search')}
                                    </Text>
                                </StyledSearchButton>
                            </>
                        )}
                    </SearchInputContainer>
                ) : isMobileView ? (
                    <IconButton
                        isDisabled={isDisabled}
                        onClick={() => setIsFolded(false)}
                        icon="magnifyingGlass"
                        color="neutral"
                        form="icon"
                    />
                ) : (
                    <RectangleButton
                        isDisabled={isDisabled}
                        onClick={() => setIsFolded(false)}
                        size={foldedButtonSize}
                        icon="magnifyingGlass"
                        form="text"
                        color="neutral"
                        label={t('button.search')}
                    />
                )}
            </SearchContainer>
        </ClickAwayListener>
    )
}

Search.displayName = 'Search'
export default Search

interface SearchContainerProps {
    isFolded: boolean
    width: string
    minWidth?: string
}
const SearchContainer = styled.div<SearchContainerProps>`
    display: flex;
    font-family: inherit;
    max-width: ${({ isFolded, width }) => (isFolded ? '120px' : width)};
    width: ${({ isFolded, width }) => (isFolded ? 'fit-content' : width)};
    min-width: ${({ isFolded, minWidth }) => (isFolded ? '0px' : minWidth)};
    overflow: hidden;
    transition: all 0.5s;
    @media screen and (max-width: 768px) {
        min-width: 0;
        transition: none;
    }
`

interface SearchInputContainerProps {
    disabled: boolean
    height: string
}
const SearchInputContainer = styled.form<SearchInputContainerProps>`
    box-sizing: border-box;
    width: 100%;
    height: ${({ height }) => height};
    padding: 0px 7px;
    border: 1px solid;
    border-color: ${({ theme }) => theme.colors.border.primary};
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[200]};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'default')};
    background: ${({ disabled, theme }) =>
        disabled ? theme.colors.surface.disablePastel : theme.colors.surface.primary};

    &:has(.search-input:focus) {
        border-color: ${({ theme }) => theme.colors.border.neutralActionActive};
    }
`

const VerticalDivider = styled.div`
    height: 20px;
    width: 1px;
    background: ${({ theme }) => theme.colors.divider.primary};
`

const StyledInput = styled.input`
    border: 0;
    flex: 1;
    outline: 0;
    font-family: inherit;
    font-size: ${({ theme }) => theme.fontSize.s};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    line-height: ${({ theme }) => theme.lineHeight.m};
    color: ${({ theme }) => theme.colors.text.primary};
    background: transparent;
    min-width: 0;
    width: 100%;

    &:disabled {
        color: ${({ theme }) => theme.colors.text.disable};
        cursor: not-allowed;
    }
    &::placeholder {
        font-size: ${({ theme }) => theme.fontSize.s};
        font-weight: ${({ theme }) => theme.fontWeight.medium};
        line-height: ${({ theme }) => theme.lineHeight.m};
        color: ${({ theme }) => theme.colors.text.placeholder};
    }
`

const StyledSearchButton = styled.button`
    cursor: pointer;
    border: 0;
    outline: 0;
    padding: 0;
    background: transparent;
    font-family: inherit;

    &:disabled {
        cursor: not-allowed;
    }
`
